import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Flex } from 'src/components/ui';
import { AuthHeader } from 'src/components/auth';
import FusionLoading from 'src/components/common/FusionLoading';
import AlertMessage from 'src/components/common/AlertMessage';
import UserContext from 'src/contexts/user/UserContext';
import authService from 'src/services/auth';
import http from 'src/services/http';
import { toastSuccess } from 'src/services/toast';
import { useRealtimeChatContext } from 'src/fusion-copilot/hooks/useRealtimeChatContext';
import {
	getValidStorageItemsWithExpiration,
	removeStorageItems,
} from 'src/lib/utils/localStorage';

const VerifyEmail = () => {
	const navigate = useNavigate();
	const { openCopilot } = useRealtimeChatContext();
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [searchParams] = useSearchParams();
	const { setUser } = useContext(UserContext);

	useEffect(() => {
		const token = searchParams.get('token');
		const priceId = searchParams.get('priceId') ?? undefined;
		const campaignParam = searchParams.get('campaign');
		const campaignIndex = campaignParam !== null ? Number(campaignParam) : undefined;
		if (!token) {
			setError('Token is invalid or expired');
			setIsLoading(false);
			return;
		}
		handleVerifyEmail(token, priceId, campaignIndex);
	}, []);

	const handleVerifyEmail = async (token: string, priceId?: string, campaignIndex?: number) => {
		try {
			const currentUser = authService.getCurrentUser();
			if (currentUser && currentUser.isEmailVerified) {
				navigate('/home');
				return;
			}

			// Get valid storage items with expiration from localStorage
			const storageData = getValidStorageItemsWithExpiration();
			const storageKeys = Object.keys(storageData);

			// Call verify-email with storage data
			const { user, redirectUrl } = await authService.verifyEmail(
				token,
				priceId,
				storageKeys.length > 0 ? storageData : undefined,
				campaignIndex,
			);

			// Remove the storage items after successful verification
			if (storageKeys.length > 0) {
				removeStorageItems(storageKeys);
			}

			if (user) {
				setUser(user);
				toastSuccess('Email verified successfully. You are now logged in.');

				try {
					await http.post('/v1/copilot/chat/new');
					console.log('[VerifyEmail] Copilot chat initialized successfully');
					sessionStorage.setItem('copilotNeedsReload', 'true');
				} catch (error) {
					console.error('[VerifyEmail] Failed to initialize copilot chat:', error);
				}

				openCopilot(true);
				await new Promise((resolve) => {
					setTimeout(resolve, 300);
				});
				sessionStorage.setItem(
					'copilotState',
					JSON.stringify({ isOpen: true, isBlocked: true }),
				);
				sessionStorage.setItem('hasOpenedCopilot', 'true');
				setTimeout(() => {
					navigate(redirectUrl ?? '/home');
				}, 100);
			}
		} catch (error: any) {
			if (!error.response) {
				console.error(error);
				return;
			}
			const status = error.response.status;
			const { message } = error.response.data;

			if (status === 409) {
				navigate('/login', {
					state: {
						error:
							'This email has already been verified. Please log in with your credentials.',
					},
				});
			} else if (status === 401 || status === 403) {
				navigate('/pending-verification');
			} else {
				setError(message);
			}
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<Container maxW="md">
			<Flex direction="column" gap={12}>
				<AuthHeader title="Verify Email" />
				{error && <AlertMessage status="error">{error}</AlertMessage>}
				<FusionLoading isLoading={isLoading} />
			</Flex>
		</Container>
	);
};

export default VerifyEmail;
